import { isEmpty } from 'lodash';
import { IChoiceGroupOption } from '@fluentui/react';
import {
    BUSINESS_CONSEQUENCE_OPTIONS,
    ESCALATION_REASON_OPTIONS,
    PAY_MORE_OPTIONS,
    REQUEST_TYPE_KEYS,
    WARRANTY_HELP_OPTIONS,
} from 'app/modules/user/createTicket/contextualDetails/types';
import { readText, resolveRequestTypeVariant, toViewRequest } from './requestFields';
import { ViewerRole } from './types';

/**
 * Builds the body of the single "Contextual Details" card on the Request
 * Details surfaces. One card, four bodies:
 *
 *  - CSS Escalation    → case number + escalation reason
 *  - Warranty          → help type, additional details, customer details, shipping address
 *  - Feature Request   → business consequence, pay-more, price uplift, competitors
 *  - Other             → nothing at all (the card is not rendered)
 *
 * Every field is hide-when-empty, so the card degrades cleanly while the
 * backend catches up on the pending `RequestDto` fields.
 *
 * The Warranty contact and shipping fields are PII: they are returned for the
 * ticket creator and admins only, never for a plain viewer.
 */

/** A single label/value pair rendered inside the card. */
export interface ContextualField {
    key: string;
    label: string;
    value: string;
}

/** A labelled group of fields (e.g. "Customer Details"). */
export interface ContextualFieldGroup {
    key: string;
    /** Sub-heading rendered above the group; omitted for the leading group. */
    label?: string;
    fields: ContextualField[];
}

/** Card title, shared by every variant. */
export const CONTEXTUAL_DETAILS_TITLE = 'Contextual Details';

/** Roles allowed to see the Warranty contact/shipping PII. */
const PII_ROLES = [ViewerRole.Creator, ViewerRole.Admin];

/**
 * Maps a stored option key onto its display text, falling back to the raw value
 * so a backend that persists the label instead of the key still renders.
 */
const optionText = (options: IChoiceGroupOption[], raw: string): string =>
    options.find((option) => option.key === raw)?.text ?? raw;

const field = (key: string, label: string, value: string): ContextualField | undefined =>
    isEmpty(value) ? undefined : { key, label, value };

const group = (
    key: string,
    fields: (ContextualField | undefined)[],
    label?: string,
): ContextualFieldGroup | undefined => {
    const present = fields.filter((item): item is ContextualField => !!item);
    return present.length === 0 ? undefined : { key, label, fields: present };
};

const compact = (groups: (ContextualFieldGroup | undefined)[]): ContextualFieldGroup[] =>
    groups.filter((item): item is ContextualFieldGroup => !!item);

/**
 * Builds the contextual-details groups for a request. Returns an empty array
 * when the card must not be rendered (the "Other" variant, or every field of a
 * typed variant being empty).
 */
export const buildContextualDetailGroups = (
    request?: unknown,
    viewerRole: ViewerRole = ViewerRole.Viewer,
): ContextualFieldGroup[] => {
    const view = toViewRequest(request);
    const variant = resolveRequestTypeVariant(view.requestType);

    switch (variant) {
        case REQUEST_TYPE_KEYS.CSS: {
            const escalationReason = readText(view.escalationReason);
            return compact([
                group('cssEscalation', [
                    field('cssCaseNumber', 'CSS Case Number', readText(view.cssCaseNumber)),
                    field(
                        'escalationReason',
                        'Why is this case escalated?',
                        isEmpty(escalationReason)
                            ? ''
                            : optionText(ESCALATION_REASON_OPTIONS, escalationReason),
                    ),
                ]),
            ]);
        }

        case REQUEST_TYPE_KEYS.WARRANTY: {
            const helpType = readText(view.warrantyHelpType);
            const company = view.companyInformation ?? {};
            const canSeePii = PII_ROLES.includes(viewerRole);

            return compact([
                group('warrantyNeed', [
                    field(
                        'warrantyHelpType',
                        'What do you need help with?',
                        isEmpty(helpType) ? '' : optionText(WARRANTY_HELP_OPTIONS, helpType),
                    ),
                    field(
                        'warrantyAdditionalDetails',
                        'Additional details',
                        readText(view.warrantyAdditionalDetails),
                    ),
                ]),
                canSeePii
                    ? group(
                          'customerDetails',
                          [
                              field('contactName', 'Contact Name', readText(company.companyContactName)),
                              field('contactEmail', 'Email', readText(company.contactEmailAddress)),
                              field('contactPhone', 'Phone', readText(company.contactPhoneNumber)),
                          ],
                          'Customer Details',
                      )
                    : undefined,
                canSeePii
                    ? group(
                          'shippingAddress',
                          [
                              // The wizard captures one free-text address, so render it as
                              // given. The mock's street/city/state/postal breakdown needs the
                              // capture to be split first — see CompanyInformationDto.
                              field(
                                  'shippingAddressLine',
                                  'Address',
                                  readText(company.companyFullShippingAddress),
                              ),
                              field('country', 'Country', readText(company.country)),
                          ],
                          'Shipping Address',
                      )
                    : undefined,
            ]);
        }

        case REQUEST_TYPE_KEYS.FEATURE: {
            const businessConsequence = readText(view.businessConsequence);
            const customersPayMore = readText(view.customersPayMore);

            return compact([
                group('featureRequest', [
                    field(
                        'businessConsequence',
                        'Business consequence',
                        isEmpty(businessConsequence)
                            ? ''
                            : optionText(BUSINESS_CONSEQUENCE_OPTIONS, businessConsequence),
                    ),
                    field(
                        'customersPayMore',
                        'Would customers pay more for this feature?',
                        isEmpty(customersPayMore) ? '' : optionText(PAY_MORE_OPTIONS, customersPayMore),
                    ),
                    field(
                        'estimatedPriceUplift',
                        'Estimated price uplift per device',
                        readText(view.estimatedPriceUplift),
                    ),
                    field('keyCompetitors', 'Key competitors', readText(view.keyCompetitors)),
                ]),
            ]);
        }

        default:
            return [];
    }
};
